import { FilterStateItem, SelectOption, ConnectProto, JobType } from "@racoon/api";
import { useT, Namespace } from "@racoon/i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createEnumFilters } from "~/shared/lib/createEnumFilters";
import { getTypeMap, getRouteStatusMap } from "~/shared/lib/translationMaps";
import { getEnumName, getEnumValue } from "@racoon/lib";

/** ---- constants ---- */

/**
 * Route statuses a single status pill stands for. Only statuses that share a label
 * need an entry — everything else matches itself. COMPLETED and FORCE_COMPLETED are
 * both "Выполнено", so they'd otherwise render as two identical pills.
 */
const ROUTE_STATUS_GROUPS: Partial<Record<ConnectProto.RouteStatus, ConnectProto.RouteStatus[]>> = {
  [ConnectProto.RouteStatus.COMPLETED]: [
    ConnectProto.RouteStatus.COMPLETED,
    ConnectProto.RouteStatus.FORCE_COMPLETED,
  ],
};

/**
 * Pill order, following the route lifecycle instead of the enum's numeric order: a
 * route is planned before a driver picks it up, so NOT_APPROVED ("Запланировано")
 * sits right after CREATED rather than last. FORCE_COMPLETED is absent — it shares
 * the "Выполнено" pill via ROUTE_STATUS_GROUPS.
 */
const ROUTE_STATUS_PILL_ORDER: ConnectProto.RouteStatus[] = [
  ConnectProto.RouteStatus.CREATED,
  ConnectProto.RouteStatus.NOT_APPROVED,
  ConnectProto.RouteStatus.IN_PROGRESS,
  ConnectProto.RouteStatus.RETURNING,
  ConnectProto.RouteStatus.COMPLETED,
  ConnectProto.RouteStatus.CANCELLED,
];

/**
 * Route statuses the selected pill narrows to. Empty for "Все" (or an unset value),
 * which doesn't narrow at all.
 */
export const getPillRouteStatuses = (value: string): ConnectProto.RouteStatus[] => {
  if (!value || value === "all") return [];
  const status = Number(value);
  if (Number.isNaN(status)) return [];
  return ROUTE_STATUS_GROUPS[status as ConnectProto.RouteStatus] ?? [status];
};

/** Statuses a route is still moving through — a bounded, current set of routes. */
const IN_FLIGHT_ROUTE_STATUSES: ConnectProto.RouteStatus[] = [
  ConnectProto.RouteStatus.CREATED,
  ConnectProto.RouteStatus.NOT_APPROVED,
  ConnectProto.RouteStatus.IN_PROGRESS,
  ConnectProto.RouteStatus.RETURNING,
];

/**
 * Whether the selected status pill needs the date picker. The in-flight statuses
 * don't: there are only ever so many routes on the road, so they're queried by
 * status alone and shown in full. "Все" and the terminal statuses keep accumulating
 * forever, so those stay windowed.
 */
export const isDateBoundedRouteStatus = (value: string) => {
  const statuses = getPillRouteStatuses(value);
  if (statuses.length === 0) return true; // "Все"
  return !statuses.some((status) => IN_FLIGHT_ROUTE_STATUSES.includes(status));
};

const fmtYMD = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const fmtISO = (d: Date) => d.toISOString();
const asStringOptions = (xs?: SelectOption[]) =>
  (xs ?? [])
    .filter(({ id, name }) => id && name && String(id).trim() && name.trim())
    .map(({ id, name }) => ({ id: String(id), name }));

/** ---- options ---- */
type Options = {
  /** Keep select filters only in URL (not in API request) */
  frontFilterOnly?: boolean; // default true
  /** Options for building FilterBlock selects */
  options?: {
    drivers?: SelectOption[];
    objects?: SelectOption[];
    customers?: SelectOption[];
  };
};

export const useRoutesUrlState = (opts: Options = { frontFilterOnly: true }) => {
  const t = useT();
  const [sp, setSp] = useSearchParams();

  const JOB_TYPE_FILTERS = useMemo(
    () =>
      createEnumFilters<JobType>(getTypeMap(t), {
        excludeKeys: [JobType.JOB_TYPE_UNDEFINED, JobType.UNRECOGNIZED],
      }),
    [t],
  );

  // Status pills shown under the controls row (registry view only). Built from the
  // explicit order above rather than the enum map, whose numeric order would put
  // "Запланировано" last.
  const ROUTE_STATUS_PILLS = useMemo(() => {
    const labels = getRouteStatusMap(t);
    return [
      { key: t(Namespace.Common, "all") ?? "Все", value: "all" as const },
      ...ROUTE_STATUS_PILL_ORDER.map((status) => ({ key: labels[status], value: status })),
    ];
  }, [t]);

  const states = useMemo<FilterStateItem[]>(
    () => [
      { key: t(Namespace.Routes, "stateSheet"), value: "sheet" },
      { key: t(Namespace.Routes, "stateRegistry"), value: "registry" },
      { key: t(Namespace.Routes, "stateTtn"), value: "ttn" },
    ],
    [t],
  );

  /** ---------- BATCHED URL PATCHER (coalesces same-tick updates) ---------- */
  // FilterBlock's "Apply" fires one change per key synchronously; react-router's setSp doesn't
  // chain across same-tick calls, so we accumulate into a buffer and flush once per frame.
  const pendingRef = useRef<URLSearchParams | null>(null);
  const flushIdRef = useRef<number | null>(null);

  const flushPatches = useCallback(() => {
    if (!pendingRef.current) return;
    const next = pendingRef.current;
    pendingRef.current = null;
    flushIdRef.current = null;
    setSp(next, { replace: true });
  }, [setSp]);

  const patchMany = useCallback(
    (changes: Record<string, string | null>) => {
      const base = pendingRef.current ?? new URLSearchParams(sp);

      for (const [k, v] of Object.entries(changes)) {
        if (v == null || v === "") base.delete(k);
        else base.set(k, v);
      }

      pendingRef.current = base;

      if (flushIdRef.current == null) {
        flushIdRef.current = requestAnimationFrame(flushPatches);
      }
    },
    [sp, flushPatches],
  );

  // Cancel any pending flush on unmount so navigating away doesn't mutate the next page's URL.
  useEffect(() => {
    return () => {
      if (flushIdRef.current != null) {
        cancelAnimationFrame(flushIdRef.current);
        flushIdRef.current = null;
        pendingRef.current = null;
      }
    };
  }, []);

  const patch = useCallback((kv: Record<string, string | null>) => patchMany(kv), [patchMany]);

  // freeze defaults on mount
  const todayRef = useRef(new Date());
  const defaultStartRef = useRef(
    new Date(
      todayRef.current.getFullYear(),
      todayRef.current.getMonth(),
      todayRef.current.getDate(),
    ),
  );

  // URL params (strings)
  const statusS = sp.get("status") ?? "all";
  const startS = sp.get("start");
  const endS = sp.get("end");
  const driverId = sp.get("driverId") ?? "";
  const objectId = sp.get("objectId") ?? "";
  const customerId = sp.get("customerId") ?? "";
  const searchS = sp.get("search") ?? "";
  const jobType = sp.get("jobType") ?? "";
  const routeStatusName = sp.get("routeStatus") ?? "";

  // routeStatus is kept in the URL as a readable enum name (e.g. "ROUTE_STATUS_NOT_APPROVED"),
  // but the rest of this hook (select options, predicate) works with the numeric enum value.
  const routeStatus = useMemo(() => {
    const value = getEnumValue(ConnectProto.RouteStatusSchema, routeStatusName);
    return value !== undefined ? String(value) : "";
  }, [routeStatusName]);

  // stable strings for API
  const startYmd = useMemo(() => startS ?? fmtYMD(defaultStartRef.current), [startS]);
  const endYmd = useMemo(() => {
    if (endS) return endS;
    const d = new Date(todayRef.current);
    d.setHours(23, 59, 59, 999);
    return fmtISO(d);
  }, [endS]);

  // UI dates for pickers
  const startDate = useMemo(() => new Date(startYmd), [startYmd]);
  const endDate = useMemo(() => new Date(endYmd), [endYmd]);

  const selectedState = states.find((s) => s.value === statusS) ?? states[0];

  /** ----- URL-driven handlers ----- */
  const onStateChange = (v: FilterStateItem) => patch({ status: v.value });

  const onDateChange = useCallback(
    (k: "startDate" | "endDate", d: Date) => {
      const date = new Date(d);
      if (k === "startDate") {
        date.setHours(0, 0, 0, 0);
      } else {
        date.setHours(23, 59, 59, 999);
      }
      patchMany({ [k === "startDate" ? "start" : "end"]: fmtISO(date) });
    },
    [patchMany],
  );

  const onFilterParamsChange = (key: string, value: string) => {
    let v: string | null = !value || value === "all" ? null : value;
    // routeStatus select options carry the numeric enum value; store the readable name instead.
    if (v && key === "routeStatus") {
      v = getEnumName(ConnectProto.RouteStatusSchema, Number(v)) ?? v;
    }
    patchMany({ [key]: v });
  };

  // Pills carry the numeric enum value; reuse the same writer as the old select so
  // the URL keeps storing the readable enum name.
  const onRouteStatusChange = (value: string) => onFilterParamsChange("routeStatus", value);

  // The input echoes keystrokes immediately; the URL — and with it the server request —
  // follows after a pause, so the backend is not queried per keystroke.
  const [searchValue, setSearchValue] = useState(searchS);
  useEffect(() => {
    setSearchValue(searchS);
  }, [searchS]);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);
  const onSearchChange = (value: string) => {
    setSearchValue(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      patch({ search: value.trim() ? value : null });
    }, 1000);
  };

  // FilterBlock params
  const filterParams = useMemo(
    () => [
      {
        key: "customerId",
        value: customerId,
        type: "select" as const,
        searchable: true,
        label: t(Namespace.Routes, "filterCustomer"),
        options: asStringOptions(opts.options?.customers),
      },
      {
        key: "objectId",
        value: objectId,
        type: "select" as const,
        searchable: true,
        label: t(Namespace.Routes, "object"),
        options: asStringOptions(opts.options?.objects),
      },
      {
        key: "driverId",
        value: driverId,
        type: "select" as const,
        searchable: true,
        label: t(Namespace.Routes, "driver"),
        options: asStringOptions(opts.options?.drivers),
      },
      {
        key: "jobType",
        value: jobType,
        type: "select" as const,
        label: t(Namespace.Routes, "filterType"),
        options: JOB_TYPE_FILTERS.map((p) => ({
          id: String(p.value),
          name: p.key,
        })),
      },
    ],
    // routeStatus is deliberately absent: it lives in the status pills row instead
    // of the filter sheet, so there's exactly one control for it.
    [customerId, objectId, driverId, opts.options, jobType, t],
  );

  return {
    // url-driven state
    states,
    selectedState,
    startDate,
    endDate,

    // status pills (registry view only)
    routeStatusPills: ROUTE_STATUS_PILLS,
    selectedRouteStatus: routeStatus || "all",
    onRouteStatusChange,

    onStateChange,
    onFilterParamsChange,
    onDateChange,
    onSearchChange,
    searchValue,
    searchQuery: searchS,
    url: { driverId, objectId, customerId, jobType },

    // server-side pagination

    // filter descriptors
    filterParams,
  };
};
